import { FormsAggregation } from '@viapeople/components'
import type {
	AggregationOptionData,
	AggregationResponseGroup,
	AggregationRoleData,
} from '@viapeople/components/src/components/forms/types'
import { useFormBuilderStore } from '../../store'
import type { FormInputComponent } from '../../types'

const MOCK_GROUP_BY_ROLE_DATA: AggregationResponseGroup[] = [
	{ role: 'Role 1 Example', responses: ['Answer 1 Example', 'Answer 2 Example'] },
	{ role: 'Role 2 Example', responses: ['Answer 1 Example', 'Answer 2 Example', 'Answer 3 Example'] },
]

const MOCK_COMBINE_DATA: AggregationResponseGroup[] = [
	{ role: 'role_1', responses: ['Answer 1 Example', 'Answer 2 Example'] },
	{ role: 'role_2', responses: ['Answer 3 Example', 'Answer 4 Example', 'Answer 5 Example'] },
]

const MOCK_OPTION_DATA: AggregationOptionData[] = [
	{ value: '1', label: 'Critical', color: '#f87171', count: 1, total: 13 },
	{ value: '2', label: 'Needs Improvement', color: '#fca5a5', count: 1, total: 13 },
	{ value: '3', label: 'Average', color: '#fdba74', count: 2, total: 13 },
	{ value: '4', label: 'Good', color: '#93c5fd', count: 6, total: 13 },
	{ value: '5', label: 'Excellent', color: '#86efac', count: 3, total: 13 },
	{ value: 'na', label: 'Not Applicable', color: '#e5e7eb', count: 0, total: 13 },
]

const MOCK_ROLE_DATA_TEXT: AggregationRoleData[] = [
	{ role: 'manager', responses: ['Answer 1 Example', 'Answer 2 Example'] },
	{ role: 'peer', responses: ['Answer 3 Example', 'Answer 4 Example', 'Answer 5 Example'] },
]

const MOCK_ROLE_DATA_OPTION: AggregationRoleData[] = [
	{ role: 'manager', optionData: MOCK_OPTION_DATA },
	{ role: 'peer', optionData: MOCK_OPTION_DATA },
]

const MOCK_ROLE_DATA_NUMERIC: AggregationRoleData[] = [
	{ role: 'manager', numericValue: 4.2 },
	{ role: 'peer', numericValue: 3.6 },
]

const MOCK_RATE_GROUP_DATA_TEXT: AggregationRoleData[] = [
	{
		role: 'manager',
		rateGroups: [
			{ rateGroup: 'Group A', responses: ['Answer 1 Example'] },
			{ rateGroup: 'Group B', responses: ['Answer 2 Example', 'Answer 3 Example'] },
		],
	},
	{
		role: 'peer',
		rateGroups: [{ rateGroup: 'Group A', responses: ['Answer 4 Example', 'Answer 5 Example'] }],
	},
]

const MOCK_RATE_GROUP_DATA_OPTION: AggregationRoleData[] = [
	{
		role: 'manager',
		rateGroups: [
			{ rateGroup: 'Group A', optionData: MOCK_OPTION_DATA },
			{ rateGroup: 'Group B', optionData: MOCK_OPTION_DATA },
		],
	},
	{
		role: 'peer',
		rateGroups: [{ rateGroup: 'Group A', optionData: MOCK_OPTION_DATA }],
	},
]

const MOCK_RATE_GROUP_DATA_NUMERIC: AggregationRoleData[] = [
	{
		role: 'manager',
		rateGroups: [
			{ rateGroup: 'Group A', numericValue: 4.5 },
			{ rateGroup: 'Group B', numericValue: 3.9 },
		],
	},
	{
		role: 'peer',
		rateGroups: [{ rateGroup: 'Group A', numericValue: 3.5 }],
	},
]

function findById(components: FormInputComponent[], id: string | null): FormInputComponent | null {
	if (!id) return null
	for (const c of components) {
		if (c.id === id) return c
		if (c.children) {
			const found = findById(c.children as FormInputComponent[], id)
			if (found) return found
		}
	}
	return null
}

const TEXT_COMPONENTS = new Set(['text', 'textarea'])
const OPTION_COMPONENTS = new Set(['checkbox', 'radio-group', 'select', '9boxrating'])
const NUMERIC_COMPONENTS = new Set(['rating', 'number'])

export function FormsAggregationPreview({ component }: { component: FormInputComponent }) {
	const formComponents = useFormBuilderStore((state) => state.formComponents)

	const { selectedComponentIds, aggregationType, grouping } = component.custom as {
		selectedComponentIds?: string[]
		aggregationType?: string
		grouping?: 'combine' | 'groupByRole' | 'groupByRoleAndRateGroup'
	}

	const hasSource = selectedComponentIds?.some(Boolean)

	const sourceComponentName = hasSource
		? (selectedComponentIds?.map((id) => findById(formComponents as FormInputComponent[], id)).find(Boolean)?.name ??
			null)
		: null

	if (!hasSource) {
		return <FormsAggregation component={component} emptyPlaceholder='The aggregation preview will appear here' />
	}

	if (sourceComponentName === 'date') {
		return (
			<FormsAggregation
				component={component}
				emptyPlaceholder='Date inputs are not supported as an aggregation source.'
			/>
		)
	}

	if (!aggregationType) {
		return <FormsAggregation component={component} emptyPlaceholder='Select an aggregation type to see the preview.' />
	}

	const isText = sourceComponentName ? TEXT_COMPONENTS.has(sourceComponentName) : false
	const isOption = sourceComponentName ? OPTION_COMPONENTS.has(sourceComponentName) : false
	const isNumeric = sourceComponentName ? NUMERIC_COMPONENTS.has(sourceComponentName) : false
	const renderAsHtml = sourceComponentName === 'textarea'

	if (grouping === 'groupByRole') {
		if (isText)
			return <FormsAggregation component={component} roles={MOCK_ROLE_DATA_TEXT} renderAsHtml={renderAsHtml} />
		if (isOption) return <FormsAggregation component={component} roles={MOCK_ROLE_DATA_OPTION} />
		if (isNumeric) return <FormsAggregation component={component} roles={MOCK_ROLE_DATA_NUMERIC} />
	}

	if (grouping === 'groupByRoleAndRateGroup') {
		if (isText)
			return <FormsAggregation component={component} roles={MOCK_RATE_GROUP_DATA_TEXT} renderAsHtml={renderAsHtml} />
		if (isOption) return <FormsAggregation component={component} roles={MOCK_RATE_GROUP_DATA_OPTION} />
		if (isNumeric) return <FormsAggregation component={component} roles={MOCK_RATE_GROUP_DATA_NUMERIC} />
	}

	// combine (default) — existing behaviour per aggregationType
	if (aggregationType === 'groupByRole') {
		return <FormsAggregation component={component} data={MOCK_GROUP_BY_ROLE_DATA} renderAsHtml={renderAsHtml} />
	}

	if (aggregationType === 'combine') {
		return <FormsAggregation component={component} data={MOCK_COMBINE_DATA} renderAsHtml={renderAsHtml} />
	}

	if (aggregationType === 'distribution' || aggregationType === 'mostVoted' || aggregationType === 'leastVoted') {
		return <FormsAggregation component={component} optionData={MOCK_OPTION_DATA} />
	}

	if (aggregationType === 'average') {
		return <FormsAggregation component={component} numericValue={3.8} />
	}

	if (aggregationType === 'max') {
		return <FormsAggregation component={component} numericValue={5} />
	}

	if (aggregationType === 'minimum') {
		return <FormsAggregation component={component} numericValue={1} />
	}

	return <FormsAggregation component={component} />
}
